
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { calculateTerraceStatus, TerracePosition, SunStatus } from '@/lib/utils/sunCalculator';

interface UseSunStatusOptions {
  autoRefresh?: boolean;
  refreshInterval?: number; // in milliseconds
}

interface SunStatusResult {
  status: SunStatus;
  lastUpdated: Date;
  sunPosition: {
    azimuth: number;
    elevation: number;
  };
  isLoading: boolean;
  error: string | null;
}

export const useSunStatus = (
  terrace: TerracePosition | null,
  options: UseSunStatusOptions = {}
) => {
  const { autoRefresh = false, refreshInterval = 15 * 60 * 1000 } = options;
  
  const [result, setResult] = useState<SunStatusResult>({
    status: 'not-sunny',
    lastUpdated: new Date(),
    sunPosition: { azimuth: 0, elevation: 0 },
    isLoading: true,
    error: null
  });

  // Memoize the calculation function to prevent unnecessary re-calculations
  const calculateLocalStatus = useCallback((terraceData: TerracePosition) => {
    try {
      const currentTime = new Date();
      const calculation = calculateTerraceStatus(terraceData, currentTime);
      
      setResult(prev => ({
        ...prev,
        status: calculation.status,
        lastUpdated: currentTime,
        sunPosition: calculation.sunPosition,
        isLoading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error calculating sun status:', error);
      setResult(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to calculate sun status'
      }));
    }
  }, []);

  const refreshFromServer = useCallback(async (terraceId: string) => {
    try {
      setResult(prev => ({ ...prev, isLoading: true, error: null }));
      
      const { data, error } = await supabase.functions.invoke('calculate-sun-status', {
        body: { terraceId }
      });

      if (error) {
        throw error;
      }

      setResult(prev => ({
        ...prev,
        status: data.status,
        lastUpdated: new Date(data.calculatedAt),
        sunPosition: data.sunPosition,
        isLoading: false,
        error: null
      }));
    } catch (error) {
      console.error('Error refreshing sun status from server:', error);
      setResult(prev => ({
        ...prev,
        isLoading: false,
        error: 'Failed to refresh sun status'
      }));
    }
  }, []);

  // Stable terrace reference to prevent effect loops
  const stableTerrace = useMemo(() => {
    if (!terrace) return null;
    return {
      lat: terrace.lat,
      lng: terrace.lng,
      orientation: terrace.orientation,
      ...(('id' in terrace) && { id: (terrace as any).id })
    };
  }, [terrace?.lat, terrace?.lng, terrace?.orientation, ('id' in (terrace || {})) ? (terrace as any).id : undefined]);

  useEffect(() => {
    if (!stableTerrace) {
      setResult(prev => ({ ...prev, isLoading: false }));
      return;
    }

    // Calculate initial status locally
    calculateLocalStatus(stableTerrace);

    // Set up auto-refresh if enabled
    if (autoRefresh) {
      const interval = setInterval(() => {
        calculateLocalStatus(stableTerrace);
      }, refreshInterval);

      return () => clearInterval(interval);
    }
  }, [stableTerrace, autoRefresh, refreshInterval, calculateLocalStatus]);

  return {
    ...result,
    refresh: stableTerrace && 'id' in stableTerrace ? 
      () => refreshFromServer(stableTerrace.id) : undefined,
    calculateLocal: stableTerrace ? 
      () => calculateLocalStatus(stableTerrace) : undefined
  };
};

// Hook for batch operations (admin use)
export const useBatchSunStatus = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [lastBatchResult, setLastBatchResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const triggerBatchCalculation = useCallback(async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const { data, error } = await supabase.functions.invoke('batch-calculate-sun-status');

      if (error) {
        throw error;
      }

      setLastBatchResult(data);
      console.log('Batch calculation completed:', data);
    } catch (error) {
      console.error('Error in batch calculation:', error);
      setError('Failed to run batch calculation');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  return {
    triggerBatchCalculation,
    isProcessing,
    lastBatchResult,
    error
  };
};
